import React, { useLayoutEffect, useRef, useState } from 'react';
import { useUIColors } from '../../hooks/useTheme';

const BASE_FONT_PX = 11;
const MIN_FONT_PX = 8;
const BASE_SWATCH_PX = 10;
const BASE_GAP_X = 14;
const BASE_GAP_ITEM = 6;
const LEGEND_ROW_HEIGHT_PX = 28;

/**
 * Single-row series legend that shrinks font (instead of wrapping) to keep chart height stable.
 */
const ChartSeriesLegend = ({ series = [], colors = {} }) => {
  const ui = useUIColors();
  const muted = ui.muted;
  const containerRef = useRef(null);
  const [fontSize, setFontSize] = useState(BASE_FONT_PX);

  useLayoutEffect(() => {
    const node = containerRef.current;
    if (!node || !series.length) {
      setFontSize(BASE_FONT_PX);
      return undefined;
    }

    let frame = 0;
    const fit = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        let next = BASE_FONT_PX;
        node.style.fontSize = `${next}px`;
        while (next > MIN_FONT_PX && node.scrollWidth > node.clientWidth + 1) {
          next -= 0.5;
          node.style.fontSize = `${next}px`;
        }
        setFontSize((prev) => (Math.abs(prev - next) < 0.01 ? prev : next));
      });
    };

    fit();
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(fit) : null;
    observer?.observe(node);
    window.addEventListener('resize', fit);
    return () => {
      cancelAnimationFrame(frame);
      observer?.disconnect();
      window.removeEventListener('resize', fit);
    };
  }, [series]);

  if (!series.length) return null;

  const scale = fontSize / BASE_FONT_PX;
  const swatch = Math.max(7, Math.round(BASE_SWATCH_PX * scale));
  const gapX = Math.max(8, Math.round(BASE_GAP_X * scale));
  const gapItem = Math.max(4, Math.round(BASE_GAP_ITEM * scale));

  return (
    <div
      ref={containerRef}
      style={{
        display: 'flex',
        flexWrap: 'nowrap',
        alignItems: 'center',
        gap: `0 ${gapX}px`,
        padding: '8px 10px 0',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        fontSize: `${fontSize}px`,
        lineHeight: 1.2,
        height: `${LEGEND_ROW_HEIGHT_PX}px`,
        boxSizing: 'border-box',
      }}
    >
      {series.map((seriesKey) => (
        <div
          key={seriesKey}
          title={seriesKey}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: `${gapItem}px`,
            fontWeight: 600,
            color: muted,
            flexShrink: 0,
          }}
        >
          <span
            style={{
              width: `${swatch}px`,
              height: `${swatch}px`,
              borderRadius: '2px',
              background: colors[seriesKey],
              flexShrink: 0,
            }}
          />
          {seriesKey}
        </div>
      ))}
    </div>
  );
};

export default ChartSeriesLegend;
